// comprasUtils.js — Helpers compartilhados pelos fluxos de Compra e Cancelamento.
// Centraliza formatação de valores/datas, rótulos de pagamento/status e o cálculo
// das etapas de entrega (stepper) exibidas na página de detalhe do pedido.
import { parseISO, format, addDays, isValid } from "date-fns";

const API_URL = process.env.REACT_APP_API;

// ===== Valores =====
// valorTotal dos pedidos é armazenado em centavos no backend (ex.: 8278 = R$ 82,78).
export const centavosParaReais = (v) => (parseFloat(v) || 0) / 100;

// Formata um número (em reais) como "R$ 1.234,56".
export const fmtBRL = (reais) =>
  "R$ " +
  (parseFloat(reais) || 0).toLocaleString("pt-BR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

// Formata um preço unitário simples como "30,99" (sem o prefixo R$).
export const fmtPreco = (p) =>
  (parseFloat(p) || 0).toLocaleString("pt-BR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

// ===== Datas =====
// parseISO (não `new Date`) para lidar com DATETIME do MySQL ("2026-06-10 14:30:00")
// de forma consistente entre navegadores — usado tanto na exibição quanto nos filtros.
export const toDate = (value) => {
  if (!value) return null;
  const d = typeof value === "string" ? parseISO(value) : new Date(value);
  return isValid(d) ? d : null;
};

export const formatDate = (value) => {
  const d = toDate(value);
  return d ? format(d, "dd/MM/yyyy") : "-";
};

/**
 * Data COM HORA, para o registro de compras (pedido do dono, 11/09/2026).
 *
 * Só a data não separa duas compras do mesmo dia — e comprar dois pacotes de
 * Ticket na mesma tarde é o caso comum de quem erra a quantidade e refaz. A
 * hora é o que diz qual veio primeiro.
 */
export const formatDateTime = (value) => {
  const d = toDate(value);
  return d ? format(d, "dd/MM/yyyy 'às' HH:mm") : "-";
};

// Previsão de entrega = data da compra + prazo do frete (padrão 20 dias úteis).
export const previsaoEntrega = (createdAt, dias) => {
  const base = toDate(createdAt) || new Date();
  const prazo = parseInt(dias, 10) || 20;
  return format(addDays(base, prazo), "dd/MM/yyyy");
};

// ===== Livros / autores =====
export const autorDe = (item) => {
  const a = item?.autores;
  if (Array.isArray(a) && a.length)
    return a.map((x) => (typeof x === "object" ? x?.nome : x)).join(", ");
  return item?.autor || "";
};

export const capaUrl = (item) =>
  item?.image ? `${API_URL}public/${item.image}` : null;

// ===== Pagamento =====
export const PAYMENT_LABELS = {
  credit_card: "Cartão de Crédito",
  debit_card: "Cartão de Débito",
  pix: "PIX",
  boleto: "Boleto",
  // `infinitepay` É um valor válido da coluna desde a migration
  // 2026_08_18_000000_add_infinitepay_to_payment_method_enum, e é o que TODO
  // pedido de livro grava hoje. Sem esta linha o mapa não reconhecia o método
  // mais usado da loja e o detalhe do pedido mostrava um travessão no lugar da
  // forma de pagamento.
  //
  // O rótulo não escolhe entre cartão e PIX porque o pedido não sabe: o link da
  // InfinitePay oferece os dois e o retorno não diz qual foi. Dizer "Cartão de
  // Crédito" seria adivinhar na cara do comprovante.
  infinitepay: "Cartão ou PIX (InfinitePay)",
};

export const paymentLabel = (method) => PAYMENT_LABELS[method] || "-";

/**
 * O PAGAMENTO DO PEDIDO, COM AS COINS DENTRO.
 *
 * O bloco "Método de Pagamento" lia só `payment_method`, e com isso uma compra
 * paga com Insigne Coins — inteira ou em parte — não tinha onde aparecer. O
 * dono pediu (11/09/2026) que a tela "fale a forma de pagamento incluindo se
 * for por coin".
 *
 * QUEM DIZ QUANTAS COINS: o campo `coins_gastas`, que o servidor calcula a
 * partir do extrato (PedidoEditoraController::index). A coluna
 * `pedido_editoras.insigneCoins` guarda o que o navegador ENVIOU, e o checkout
 * debita só o necessário — ela serve para saber SE houve coin, nunca QUANTA.
 *
 * QUEM DIZ QUE FOI SÓ COIN: o total cobrado, e não o `payment_method`. O
 * checkout grava 'infinitepay' no pedido ANTES de descobrir que o saldo cobriu
 * tudo (`PagarmeController::criarPedido` cria o pedido e só então chama
 * `_finalizeFreeOrder`). Total zerado é a marca de que nenhum gateway foi
 * acionado; sem ela, acrescentar o rótulo da InfinitePay acima faria uma compra
 * paga com moeda da casa dizer "Cartão".
 */
export const pagamentoDoPedido = (order) => {
  const coins = Math.max(0, Math.floor(Number(order?.coins_gastas) || 0));
  const metodo = order?.payment_method || "";
  const totalCentavos = Number(order?.valorTotal) || 0;
  const soCoins = totalCentavos <= 0 && (coins > 0 || Number(order?.insigneCoins) > 0);

  return {
    // Quantas coins saíram do saldo. Pode ser 0 num pedido antigo pago só com
    // coins: até 20/08/2026 o débito era gravado sem origem, e o extrato não
    // sabe mais a qual pedido pertence. Nesse caso a tela diz "Insigne Coins"
    // sem número — que é o que se sabe — em vez de inventar um.
    coins,
    soCoins,
    // A linha principal do bloco: "Insigne Coins" quando o saldo pagou tudo, e
    // a forma do gateway nos outros casos (inclusive no pagamento em parte, em
    // que as coins entram numa segunda linha).
    rotulo: soCoins ? "Insigne Coins" : paymentLabel(metodo),
    icone: soCoins ? "bi-coin" : metodo === "pix" ? "bi-qr-code" : "bi-credit-card-2-front",
  };
};

// Detecta a bandeira do cartão a partir do número (uso visual nos modais).
export const detectarBandeira = (numero = "") => {
  const n = numero.replace(/\D/g, "");
  if (/^4/.test(n)) return "visa";
  if (/^(5[1-5]|2[2-7])/.test(n)) return "mastercard";
  if (/^3[47]/.test(n)) return "amex";
  if (/^(606282|3841|38|60)/.test(n)) return "hipercard";
  if (/^(4011|4312|438935|451416|5041|5066|5067|509|6277|6362|6363|650|6516|6550)/.test(n)) return "elo";
  return null;
};

export const mascararCartao = (numero = "") => {
  const n = numero.replace(/\D/g, "");
  return n.slice(-4) ? `**** ${n.slice(-4)}` : "**** ****";
};

// ===== Status de entrega (stepper da página de detalhe) =====
// Etapas exibidas no mockup "Fluxo de CompraCancelar".
export const DELIVERY_STEPS = [
  { key: "compra", label: "Compra", icon: "bi-bag-check" },
  { key: "em_preparacao", label: "Em Produção", icon: "bi-box-seam" },
  { key: "enviado", label: "Enviado", icon: "bi-truck" },
  { key: "saida", label: "Saída para Entrega", icon: "bi-truck-front" },
  { key: "entregue", label: "Entregue", icon: "bi-house-check" },
];

// Índice da etapa atual a partir do status_entrega do pedido.
const STATUS_TO_INDEX = {
  pendente: 0,
  em_preparacao: 1,
  enviado: 2,
  saida_entrega: 3,
  entregue: 4,
};

/**
 * ESTE PEDIDO TEM ALGO PARA ENTREGAR?
 *
 * Só quando existe pelo menos um item impresso. O dono viu (11/09/2026) um
 * pedido de e-book desenhando a trilha de cinco etapas — "Em Produção", "Saída
 * para Entrega" — para um arquivo que já estava na conta dele desde o
 * pagamento. Pacote de Ticket e de Coins é pior ainda: não existe caixa
 * nenhuma, e a tela prometia um caminhão.
 *
 * `buildDeliverySteps` continua respondendo a quem perguntar; o que muda é que
 * a tela só pergunta quando a resposta significa alguma coisa.
 */
export const pedidoTemEntrega = (order) => {
  // Pedido de crédito nunca tem logística, mesmo que `livros` venha com lixo:
  // o que ele entrega é saldo, e saldo não viaja.
  const tipo = String(order?.tipo || "");
  if (tipo === "tickets" || tipo === "coins") return false;

  const itens = Array.isArray(order?.livros) ? order.livros : [];
  return itens.some((item) => item?.formato === "fisico");
};

// Constrói as etapas com estado (concluída/atual) e data estimada por etapa.
// Chamar isto só faz sentido quando `pedidoTemEntrega(order)` é verdadeiro —
// quem decide é a tela, e a assinatura daqui não muda por causa disso.
export const buildDeliverySteps = (order) => {
  const created = order?.created_at;
  const freteDias = order?.frete_dias;
  const baseIdx = STATUS_TO_INDEX[order?.status_entrega] ?? 0;
  // Pedido só de e-book não tem logística física: considera "compra" concluída.
  const currentIdx = Math.max(0, baseIdx);

  return DELIVERY_STEPS.map((step, i) => {
    let data = "";
    if (i === 0) data = formatDate(created);
    else if (step.key === "enviado" && order?.shipping_date) data = formatDate(order.shipping_date);
    else if (step.key === "entregue") data = previsaoEntrega(created, freteDias);
    return {
      ...step,
      done: i < currentIdx,
      current: i === currentIdx,
      data,
    };
  });
};

// ===== Cancelamento local (stub enquanto não há endpoint no backend) =====
// Marca pedidos cujo cancelamento foi solicitado pelo cliente, persistindo em
// localStorage para que a lista/detalhe reflitam o estado mesmo sem backend.
// TODO(backend): ao criar POST pedido-editora/{id}/cancelar, passar a confiar
// no status do servidor e remover esta camada local.
const CANCEL_KEY = "insigne_pedidos_cancelados";

export const getPedidosCancelados = () => {
  try { return JSON.parse(localStorage.getItem(CANCEL_KEY)) || {}; } catch { return {}; }
};
// A marca local é só um "hint" otimista: expira em 48h para não deixar um pedido
// preso em "Cancelamento em análise" caso o backend nunca confirme. O estado do
// servidor (cancelamento_status) sempre tem prioridade em cancelStateOf.
export const isPedidoCancelado = (id) => {
  const e = getPedidosCancelados()[String(id)];
  if (!e) return false;
  const t = new Date(e.em).getTime();
  if (!isNaN(t) && Date.now() - t > 48 * 60 * 60 * 1000) return false;
  return true;
};
export const marcarPedidoCancelado = (id, motivo) => {
  const m = getPedidosCancelados();
  m[String(id)] = { motivo: motivo || "", em: new Date().toISOString() };
  localStorage.setItem(CANCEL_KEY, JSON.stringify(m));
};

// Estado de cancelamento consolidado: prioriza o backend (cancelamento_status),
// com fallback para a marcação local (feedback imediato logo após solicitar).
// Retorna: "cancelado" | "solicitado" | "recusado" | null
export const cancelStateOf = (order) => {
  const cs = order?.cancelamento_status;
  if (order?.status === "Cancelado" || cs === "aprovado") return "cancelado";
  if (cs === "recusado") return "recusado";
  if (cs === "solicitado" || isPedidoCancelado(order?.id)) return "solicitado";
  return null;
};

// Rótulo amigável do status do pedido (badge da lista de compras).
export const statusPedidoLabel = (order) => {
  const cancel = cancelStateOf(order);
  if (cancel === "cancelado") return "Cancelado";
  if (cancel === "solicitado") return "Cancelamento solicitado";
  const map = {
    pendente: "Processando",
    em_preparacao: "Em Produção",
    enviado: "Enviado",
    saida_entrega: "Saída para Entrega",
    entregue: "Entregue",
  };
  if (order?.status_entrega && map[order.status_entrega]) return map[order.status_entrega];
  if (order?.status === "Pagamento confirmado") return "Pago";
  if (order?.status === "Pendente") return "Processando Pagamento";
  return order?.status || "-";
};

// Cor do badge de status (classes Tailwind) conforme o estado.
export const statusBadgeColor = (order) => {
  const cancel = cancelStateOf(order);
  if (cancel === "cancelado") return "bg-red-500/15 text-red-300 border border-red-400/30";
  if (cancel === "solicitado" || order?.status === "Pendente") return "bg-amber-400/15 text-amber-300 border border-amber-400/30";
  return "bg-emerald-400/15 text-emerald-300 border border-emerald-400/30";
};
